import React, {useState} from "react";
import {motion} from "framer-motion";
import {formatDate} from "../../../util/DateUtil";
import {AdminTransaction, Trace, TransactionPage, TransactionService} from "../../../service/TransactionService";
import usePagination from "../../../hooks/usePagination";
import PaginationLoadMore from "../../ui/pagination/PaginationLoadMore";

interface TransactionsListProps {
    transactions: TransactionPage;
}

const TransactionsList: React.FC<TransactionsListProps> = ({transactions: initialTransactions}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [activeFilter, setActiveFilter] = useState<string>("all");
    const [showTraceModal, setShowTraceModal] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<AdminTransaction | null>(null);

    const {
        items: transactions,
        setItems: setTransactions,
        loadMore,
        nextPageKey,
        setNextPageKey,
        sentinelRef,
        isAutoLoadEnabled
    } = usePagination<AdminTransaction, any>(TransactionService.getAdminTransactions, initialTransactions.items, initialTransactions.nextPageKey, true, activeFilter);

    const fetchTransactions = async (filter: string) => {
        setLoading(true);
        try {
            const data = await TransactionService.getAdminTransactions(undefined, filter);
            setTransactions(data.items);
            setNextPageKey(data.nextPageKey);
            setError(null);
        } catch (err) {
            console.error("Błąd podczas pobierania transakcji:", err);
            setError("Nie udało się załadować listy transakcji. Spróbuj ponownie później.");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filter: string) => {
        setActiveFilter(filter);
        fetchTransactions(filter);
    };

    const getStatusClass = (status: string) => {
        switch (status) {
            case "STARTED":
                return "text-primary";
            case "PAID":
                return "text-warning";
            case "COMPLETED":
                return "text-success";
            case "CHARGEBACK":
                return "text-danger";
            default:
                return "text-muted";
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case "STARTED":
                return "Utworzona";
            case "PAID":
                return "Opłacona";
            case "COMPLETED":
                return "Zakończona";
            case "CHARGEBACK":
                return "Zwrócona";
            default:
                return status;
        }
    };

    const handleShowTraceModal = (transaction: AdminTransaction) => {
        setSelectedTransaction(transaction);
        setShowTraceModal(true);
    };

    const handleCloseTraceModal = () => {
        setShowTraceModal(false);
        setSelectedTransaction(null);
    };

    // Function to get device icon based on mobile and platform
    const getDeviceIcon = (trace: Trace) => {
        if (trace.mobile && trace.mobile !== '?0' && trace.mobile !== 'unknown') {
            return <i className="bi bi-phone text-warning"></i>;
        } else if (trace.platform && trace.platform.includes('Mac')) {
            return <i className="bi bi-apple text-light"></i>;
        } else if (trace.platform && trace.platform.includes('Win')) {
            return <i className="bi bi-windows text-info"></i>;
        } else {
            return <i className="bi bi-question-diamond-fill text-light"></i>;
        }
    };

    // Function to get trace icon based on traceType
    const getTraceTypeIcon = (trace: Trace) => {
        switch (trace.traceType) {
            case 'PageLoad':
                return <i className="bi bi-window text-info"></i>;
            case 'RequestSend':
                if (trace.additionalInfo === 'InitTransaction') {
                    return <i className="bi bi-cart text-warning"></i>;
                } else {
                    return <i className="bi bi-send text-primary"></i>;
                }
            case 'EmailOpen':
                return <i className="bi bi-envelope-open text-warning"></i>;
            case 'EmailClick':
                return <i className="bi bi-envelope-check text-success"></i>;
            case 'CompleteTransaction':
                return <i className="bi bi-bag-check text-success"></i>;
            default:
                return <i className="bi bi-activity text-light"></i>;
        }
    };

    return (
        <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.4}}
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0 text-light">Szczegóły transakcji</h3>
                <motion.button
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                    className="btn btn-success"
                    onClick={() => fetchTransactions(activeFilter)}
                >
                    Odśwież
                </motion.button>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center my-5">
                    <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Ładowanie...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : (
                <div className="card shadow border-0 mb-4 bg-dark text-light">
                    <div className="card-header bg-dark border-secondary">
                        <h5 className="mb-0">Filtruj transakcje</h5>
                        <div className="d-flex flex-wrap mt-3">
                            <motion.button
                                whileHover={{scale: 1.03}}
                                whileTap={{scale: 0.97}}
                                className={`btn me-2 mb-2 ${activeFilter === "all" ? "btn-success" : "btn-outline-light"}`}
                                onClick={() => handleFilterChange("all")}
                            >
                                Wszystkie
                            </motion.button>
                            <motion.button
                                whileHover={{scale: 1.03}}
                                whileTap={{scale: 0.97}}
                                className={`btn me-2 mb-2 ${activeFilter === "created" ? "btn-primary" : "btn-outline-light"}`}
                                onClick={() => handleFilterChange("created")}
                            >
                                Utworzone
                            </motion.button>
                            <motion.button
                                whileHover={{scale: 1.03}}
                                whileTap={{scale: 0.97}}
                                className={`btn me-2 mb-2 ${activeFilter === "paid" ? "btn-warning" : "btn-outline-light"}`}
                                onClick={() => handleFilterChange("paid")}
                            >
                                Opłacone
                            </motion.button>
                            <motion.button
                                whileHover={{scale: 1.03}}
                                whileTap={{scale: 0.97}}
                                className={`btn me-2 mb-2 ${activeFilter === "completed" ? "btn-success" : "btn-outline-light"}`}
                                onClick={() => handleFilterChange("completed")}
                            >
                                Zakończone
                            </motion.button>
                            <motion.button
                                whileHover={{scale: 1.03}}
                                whileTap={{scale: 0.97}}
                                className={`btn mb-2 ${activeFilter === "chargeback" ? "btn-danger" : "btn-outline-light"}`}
                                onClick={() => handleFilterChange("chargeback")}
                            >
                                Zwrócone
                            </motion.button>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-dark table-hover mb-0">
                                <thead className="bg-dark">
                                <tr>
                                    <th scope="col">ID</th>
                                    <th scope="col">Data utworzenia</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">Imię</th>
                                    <th scope="col">Produkt</th>
                                    <th scope="col">Cena</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Pierwsze źródło</th>
                                    <th scope="col">Źródło</th>
                                    <th scope="col">Ścieżka</th>
                                </tr>
                                </thead>
                                <tbody>
                                {transactions.length > 0 ? (
                                    transactions.map((transaction) => (
                                        <motion.tr
                                            key={transaction.id}
                                            initial={{opacity: 0}}
                                            animate={{opacity: 1}}
                                            transition={{duration: 0.3}}
                                        >
                                            <td className="text-monospace small">{transaction.id}</td>
                                            <td>{formatDate(transaction.createdAt)}</td>
                                            <td>{transaction.email}</td>
                                            <td>{transaction.name}</td>
                                            <td>{transaction.description}</td>
                                            <td>{transaction.price} zł</td>
                                            <td>
                                               <span className={`${getStatusClass(transaction.status)}`}>
                                                 {getStatusText(transaction.status)}
                                               </span>
                                            </td>
                                            <td>{transaction.firstSource || "-"}</td>
                                            <td>{transaction.source || "-"}</td>
                                            <td>
                                                {transaction.traces && transaction.traces.length > 0 && (
                                                    <motion.button
                                                        whileHover={{scale: 1.2}}
                                                        whileTap={{scale: 0.9}}
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => handleShowTraceModal(transaction)}
                                                        title="Zobacz ścieżkę"
                                                    >
                                                        <i className="bi bi-info-circle"></i>
                                                    </motion.button>
                                                )}
                                            </td>
                                        </motion.tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="text-center py-4 text-light">
                                            Brak transakcji do wyświetlenia
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <PaginationLoadMore
                isAutoLoadEnabled={isAutoLoadEnabled}
                nextPageKey={nextPageKey}
                loadMore={loadMore}
                sentinelRef={sentinelRef}
            />

            {/* Trace Modal */}
            {showTraceModal && (
                <div className="modal fade show" style={{display: 'block'}} tabIndex={-1} role="dialog"
                     aria-labelledby="traceModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content bg-dark text-light">
                            <div className="modal-header border-secondary">
                                <h5 className="modal-title" id="traceModalLabel">Ścieżka użytkownika
                                    - {selectedTransaction?.email}</h5>
                                <button type="button" className="btn-close btn-close-white"
                                        onClick={handleCloseTraceModal} aria-label="Zamknij"></button>
                            </div>
                            <div className="modal-body">
                                {selectedTransaction && selectedTransaction.traces && (
                                    <div className="trace-path-container">
                                        <div className="d-flex mb-3 align-items-center">
                                            <div className="badge bg-primary text-dark me-2">Pierwsze
                                                źródło: {selectedTransaction.firstSource || 'nieznane'}</div>
                                            <div className="badge bg-warning text-dark">Źródło
                                                transakcji: {selectedTransaction.source || 'nieznane'}</div>
                                        </div>

                                        {selectedTransaction.traces.map((trace, index) => (
                                            <React.Fragment key={index}>
                                                <motion.div
                                                    initial={{opacity: 0, x: -20}}
                                                    animate={{opacity: 1, x: 0}}
                                                    transition={{delay: index * 0.1}}
                                                    className="trace-item mb-2 p-3 border border-secondary rounded"
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="me-3">
                                                            <span className="badge rounded-pill bg-dark fs-12">
                                                                {getTraceTypeIcon(trace)} {trace.additionalInfo || trace.traceType}
                                                            </span>
                                                        </div>

                                                        <div className="flex-grow-1">
                                                            <div className="d-flex align-items-center">
                                                                <strong
                                                                    className="me-2 text-info">{trace.pathname}</strong>
                                                            </div>

                                                            <div
                                                                className={trace.pathname === '' ? "mt-0 d-flex flex-wrap" : "mt-1 d-flex flex-wrap"}>
                                                                {trace.source && trace.source !== 'unknown' && (
                                                                    <span
                                                                        className="badge bg-warning text-dark me-2 mb-1">
                                                                        Źródło: {trace.source}
                                                                    </span>
                                                                )}

                                                                {trace.experimentId && (
                                                                    <>
                                                                        <span
                                                                            className="badge bg-info text-dark me-2 mb-1">
                                                                            Eksperyment: {trace.experimentId}
                                                                        </span>
                                                                        {trace.variation && (
                                                                            <span
                                                                                className="badge bg-primary text-dark mb-1">
                                                                                Wariant: {trace.variation}
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="ms-auto d-flex flex-column align-items-end">
                                                            <div className="device-info">
                                                                {getDeviceIcon(trace)}
                                                            </div>
                                                            <small
                                                                className="text-secondary-light">{formatDate(trace.timestamp)}</small>
                                                        </div>
                                                    </div>
                                                </motion.div>

                                                {/* Arrow between trace items */}
                                                {index < selectedTransaction.traces!!.length - 1 && (
                                                    <div className="d-flex justify-content-center my-1">
                                                        <i className="bi bi-arrow-down text-success fs-6"></i>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}

                                        {selectedTransaction.traces.length > 0 && (
                                            <div className="text-center mt-3">
                                                <span className="badge bg-success text-dark">
                                                    <i className="bi bi-flag-fill me-1"></i>
                                                    Transakcja {getStatusText(selectedTransaction.status).toLowerCase()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-secondary">
                                <motion.button
                                    whileHover={{scale: 1.05}}
                                    whileTap={{scale: 0.95}}
                                    className="btn btn-secondary"
                                    onClick={handleCloseTraceModal}
                                >
                                    Zamknij
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal backdrop */}
            {showTraceModal && (
                <div className="modal-backdrop fade show"></div>
            )}
        </motion.div>
    );
};

export default TransactionsList;
